'use client'

import * as React from 'react'
import { cn, useMediaQuery } from '@segway/core'

import { DatePickerCalendar, DatePickerCalendarProps } from './datePickerCalendar'
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  required?: boolean
  floatingLabel?: boolean
  error?: boolean
  leftIcon?: React.ReactNode
  leftIconClassName?: string
  rightIcon?: React.ReactNode
  rightIconClassName?: string
  labelClassName?: string
  onRightIconClick?: () => void
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label = '',
      required = false,
      floatingLabel = true,
      disabled = false,
      error = false,
      leftIcon,
      leftIconClassName,
      rightIcon,
      rightIconClassName,
      labelClassName,
      onRightIconClick,
      onFocus,
      onBlur,
      onChange,
      value,
      type,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false)
    const [hasValue, setHasValue] = React.useState(false)
    const inputRef = React.useRef<HTMLInputElement>(null)
    const calendarRef = React.useRef<HTMLDivElement>(null)
    const responsive = useMediaQuery()
    const [calendarOpen, setCalendarOpen] = React.useState(false)

    const isDatePicker = React.useMemo(() => type === 'date', [type])
    const rightIconWithHandle = React.useMemo(() => {
      if (rightIcon) return rightIcon
      if (isDatePicker)
        return (
          <svg
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 20L4.5 7L20.5 7L20.5 20H4.5Z" stroke="#86868B" stroke-width="2.05714" />
            <path d="M7.5 11H11.5" stroke="#86868B" stroke-width="2.05714" />
            <path d="M8.5 6V4" stroke="#86868B" stroke-width="2.05714" />
            <path d="M17.5 6V4" stroke="#86868B" stroke-width="2.05714" />
          </svg>
        )

      return null
    }, [isDatePicker, rightIcon])

    const shouldFloat = React.useMemo(() => {
      return label && floatingLabel && (isFocused || hasValue)
    }, [label, floatingLabel, isFocused, hasValue])

    // Combine the forwarded ref with our local ref
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    // Check if input has value on mount and when value changes
    React.useEffect(() => {
      setHasValue(!!value)
    }, [value])

    // Close the calendar when clicking outside of it
    React.useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
          setCalendarOpen(false)
        }
      }
      document.addEventListener('click', handler)
      return () => document.removeEventListener('click', handler)
    }, [])

    // set focus state automatically
    React.useEffect(() => {
      setIsFocused(calendarOpen)
    }, [calendarOpen])

    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true)
        onFocus?.(e)
      },
      [onFocus],
    )

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (!calendarOpen) setIsFocused(false)
        onBlur?.(e)
      },
      [onBlur, calendarOpen],
    )

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setHasValue(!!e.target.value)
        if (isDatePicker) {
          const val = e.target.value?.replace(/[^\d-]/g, '')?.slice(0, 10)
          const digitsPattern = /^\d{0,8}$/
          // const datePattern = /^\d{0,4}-?\d{0,2}-?\d{0,2}$/
          if (digitsPattern.test(val)) {
            const year = val.slice(0, 4)
            const month = val.slice(4, 6)
            const day = val.slice(6, 8)
            e.target.value = val.length === 8 ? `${year}-${month}-${day}` : val
          } else {
            e.target.value = val
          }
          setCalendarOpen(false)
        }
        onChange?.(e)
      },
      [onChange, isDatePicker],
    )

    const handleRightIconClick = React.useCallback<React.MouseEventHandler<HTMLDivElement>>(
      (e) => {
        e.stopPropagation()
        // disabled || !onRightIconClick ? undefined : onRightIconClick
        if (disabled) return
        if (isDatePicker) {
          setCalendarOpen((prev) => !prev)
        }
        onRightIconClick?.()
      },
      [disabled, isDatePicker, onRightIconClick, setCalendarOpen],
    )

    const handleCalendarClick = React.useCallback<DatePickerCalendarProps['handleCalendarChange']>(
      (value) => {
        if (onChange) {
          const syntheticEvent = {
            target: { value },
          } as React.ChangeEvent<HTMLInputElement>
          onChange(syntheticEvent)
        }

        setCalendarOpen(false)
        setIsFocused(false)
      },
      [onChange],
    )

    return (
      <div className="relative" ref={calendarRef}>
        <div className="relative">
          <input
            ref={inputRef}
            className={cn(
              'flex h-[4.8rem] w-full rounded-md border font-miSansRegular330 text-base text-black focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
              floatingLabel ? 'placeholder:text-transparent' : '',
              shouldFloat ? 'pb-2 pt-6' : 'py-2',
              rightIcon ? 'pr-10' : 'pr-4',
              leftIcon ? 'pl-[30px] xl:pl-[50px]' : 'pl-4',
              disabled ? 'cursor-not-allowed bg-gray-0 text-gray-5' : 'bg-white',
              error
                ? 'border-primary focus-visible:border-primary'
                : 'border-gray-2 focus-visible:border-black',
              className,
            )}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            disabled={disabled}
            value={value}
            type={isDatePicker ? 'text' : type}
            {...props}
          />
          {label && (
            <label
              className={cn(
                'pointer-events-none absolute left-4 font-miSansRegular330 transition-all duration-200',
                shouldFloat ? 'top-1.5 text-sm' : 'top-1/2 -translate-y-1/2 text-base',
                !floatingLabel && hasValue ? 'opacity-0' : '',
                disabled ? 'text-gray-5' : error ? 'text-gray' : 'text-gray',
                leftIcon ? 'left-10' : 'left-4',
                labelClassName,
              )}>
              {label}
              {required && (
                <span className={cn('ml-0.5', disabled ? 'text-gray-5' : 'text-primary')}>*</span>
              )}
            </label>
          )}

          {calendarOpen && (
            <DatePickerCalendar
              value={value as string}
              className="top-[calc(100% + 4px)] absolute left-0"
              handleCalendarChange={handleCalendarClick}
            />
          )}

          {leftIcon && (
            <div
              className={cn(
                `absolute left-3 ${responsive && responsive.xl ? 'top-1/2' : 'top-[48%]'} -translate-y-1/2 xl:left-4`,
                leftIconClassName,
              )}>
              {leftIcon}
            </div>
          )}

          {rightIconWithHandle && (
            <div
              className={cn(
                'absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer',
                onRightIconClick && !disabled ? 'cursor-pointer' : '',
                disabled ? 'opacity-70' : '',
                rightIconClassName,
              )}
              onClick={handleRightIconClick}>
              {rightIconWithHandle}
            </div>
          )}
        </div>
      </div>
    )
  },
)

Input.displayName = 'Input'

export default Input
